import React, { useState, useEffect, useRef } from 'react';
import { useAnimation } from 'framer-motion6';
import useWindowSize from 'hooks/useWindowSize';
import { useInView } from 'react-intersection-observer';
import { SCREEN_SIZES } from 'style';
import { SLIDES, SLIDES_MOBILE } from './consts';
import { Curve, TimelineContainer, Track, TrackWrapper } from './styled';
import Slide from './components/Slide';

const marginRight = 60;

const getClosestSlide = (counts, goal) => {
  return counts.reduce((prev, curr) => {
    return Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev;
  });
};

const getSlideOffsets = (slides, windowWidth) => {
  const slideWidths = slides.map((slide) => slide.width + marginRight);

  let offsets = [0].concat(
    slideWidths.map((slide, index) =>
      slideWidths.slice(0, index + 1).reduce((a, b) => a + b, 0)
    )
  );

  offsets.pop();

  offsets = offsets.map((offset, i) => {
    return offset - windowWidth / 2 + slides[i].width / 2;
  });

  return offsets;
};

const Timeline = () => {
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth <= SCREEN_SIZES.tabletLandscape;

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [slides, setSlides] = useState(isMobile ? SLIDES_MOBILE : SLIDES);
  const [slideOffsets, setSlideOffsets] = useState(getSlideOffsets(slides, windowWidth));

  const wrapperRef = useRef();
  const trackControls = useAnimation();
  const { ref, inView } = useInView();

  const handleDrag = (target) => {
    const snapTarget = getClosestSlide(slideOffsets, target * -1) * -1;

    const activeSlideIndex = slideOffsets.findIndex(
      (slideOffset) => -slideOffset === snapTarget
    );

    setActiveSlideIndex(activeSlideIndex);
    return snapTarget;
  };

  const goToSlide = (index) => {
    const target = slideOffsets[index];

    trackControls.start({
      x: -target,
      transition: { duration: 0.8 }
    });

    setActiveSlideIndex(index);
  };

  useEffect(() => {
    if (isDragging) {
      setTimeout(() => {
        setIsDragging(false);
      }, 700);
    }
  }, [isDragging]);

  useEffect(() => {
    goToSlide(2);
  }, [slideOffsets, slides]);

  useEffect(() => {
    setSlideOffsets(getSlideOffsets(slides, windowWidth));
  }, [windowWidth]);

  useEffect(() => {
    if (isMobile) {
      setSlides(SLIDES_MOBILE);
    } else {
      setSlides(SLIDES);
    }
  }, [isMobile]);

  return (
    <>
      <TimelineContainer>
        <TrackWrapper ref={wrapperRef}>
          <Track
            onClick={(e) => e.stopPropagation()}
            onDrag={() => setIsDragging(true)}
            drag={!isMobile ? 'x' : null}
            ref={ref}
            animate={trackControls}
            dragTransition={{
              power: 0.01,
              timeConstant: 200,
              modifyTarget: handleDrag
            }}
          >
            {slides.map((slide, slideIndex) => (
              <Slide
                inView={inView}
                key={slide?.id}
                slide={slide}
                slideIndex={slideIndex}
                marginRight={marginRight}
                isActiveSlide={activeSlideIndex === slideIndex}
                activeSlideIndex={activeSlideIndex}
                isLastSlide={slideIndex === slides.length - 1}
                isFirstSlide={slideIndex === 0}
                goToSlide={(index) => goToSlide(index)}
                isDragging={isDragging}
                slides={slides}
              />
            ))}
          </Track>
        </TrackWrapper>
      </TimelineContainer>
      <Curve />
    </>
  );
};

export default Timeline;
